import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { domainError, errorMessage, useTRPC } from "../../lib/api";
import { usePresence } from "../../lib/use-presence";
import { useToast } from "../../ui/toast";

/**
 * Approve and reject. Approving a send or destructive action needs presence, so it first makes
 * sure a passkey-backed token is in memory (Touch ID if not), then calls `approvals.decide`.
 * Without a passkey it takes the person to Settings → Security to add one.
 */
export function useDecide() {
  const trpc = useTRPC();
  const toast = useToast();
  const navigate = useNavigate();
  const { ensurePresence, lock } = usePresence();
  const decide = useMutation(trpc.approvals.decide.mutationOptions());
  const [busyId, setBusyId] = useState<string | null>(null);

  const fail = (error: unknown) => {
    // A token the api no longer accepts (e.g. it rotated its key) is useless: ask again next time.
    if (domainError(error)?.reason === "presence_required") lock();
    toast({ tone: "error", message: errorMessage(error) });
  };

  async function approve(approvalId: string): Promise<void> {
    setBusyId(approvalId);
    try {
      const outcome = await ensurePresence();
      if (outcome === "no_passkey") {
        toast({ message: "Approving needs a passkey (Touch ID or a security key). Add one, then come back." });
        await navigate({ to: "/settings/security" });
        return;
      }
      if (outcome === "cancelled") {
        toast({ message: "Not approved: the passkey check was cancelled." });
        return;
      }
      await decide.mutateAsync({ approvalId, approve: true });
      toast({ message: "Approved." });
    } catch (error) {
      fail(error);
    } finally {
      setBusyId(null);
    }
  }

  async function reject(approvalId: string): Promise<void> {
    setBusyId(approvalId);
    try {
      await decide.mutateAsync({ approvalId, approve: false });
      toast({ message: "Rejected." });
    } catch (error) {
      // Rejecting an expired request records it as expired; that is what the person wanted.
      if (domainError(error)?.reason === "approval_expired") toast({ message: "It had expired; moved to History." });
      else fail(error);
    } finally {
      setBusyId(null);
    }
  }

  return { approve, reject, busyId };
}
